// API base URL. Default points to the production custom domain
// (api.rjnetwork.site). Override via VITE_API_URL at build time for local dev
// or staging. The legacy *.workers.dev URL still resolves but is being phased
// out — do not hardcode it here.
const API_URL = (import.meta.env.VITE_API_URL as string) ?? 'https://api.rjnetwork.site';

/**
 * Full auth tear-down on 401. Clears both the raw token key and the Zustand
 * persist snapshot so rehydration on next load starts clean.
 *
 * Avoids importing useAuth at module-top to prevent circular dependency
 * (auth.ts imports api.ts). Instead we reach into localStorage directly and
 * let the Zustand store reconcile on the next mount.
 */
function clearAuthAndRedirect() {
  // Remove both the raw JWT and the Zustand persist snapshot so the next page
  // load starts unauthenticated regardless of how rehydration is triggered.
  localStorage.removeItem('rj_token');
  localStorage.removeItem('rj_auth');
  setToken(null);
  if (location.pathname !== '/login') location.href = '/login';
}

interface ApiOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  headers?: Record<string, string>;
}

export class ApiError extends Error {
  constructor(public code: string, message: string, public status: number, public details?: unknown) {
    super(message);
  }
}

function getToken(): string | null {
  return localStorage.getItem('rj_token');
}

export function setToken(token: string | null) {
  if (token) localStorage.setItem('rj_token', token);
  else localStorage.removeItem('rj_token');
}

/**
 * Unduh file biner/teks (CSV export) dengan auth header — apiClient.get()
 * selalu parse JSON, jadi tidak bisa dipakai untuk attachment.
 */
export async function apiDownload(path: string, filename: string): Promise<void> {
  const token = localStorage.getItem('rj_token');
  const resp = await fetch(`${API_URL}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!resp.ok) {
    throw new ApiError('DOWNLOAD_FAILED', `HTTP ${resp.status}`, resp.status);
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function api<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const url = new URL(`${API_URL}${path}`);
  if (options.query) {
    for (const [k, v] of Object.entries(options.query)) {
      if (v != null) url.searchParams.set(k, String(v));
    }
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers ?? {}),
  };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const resp = await fetch(url.toString(), {
    method: options.method ?? 'GET',
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  let json: any = null;
  try {
    json = await resp.json();
  } catch {
    /* ignore */
  }

  if (!resp.ok) {
    const code = json?.error?.code ?? 'UNKNOWN';
    const message = json?.error?.message ?? `HTTP ${resp.status}`;
    if (resp.status === 401) {
      // Clear both rj_token and rj_auth (Zustand persist) before redirecting
      clearAuthAndRedirect();
    }
    throw new ApiError(code, message, resp.status, json?.error?.details);
  }

  return (json?.data ?? json) as T;
}

// Like `api`, but returns the full envelope `{data, meta}` (used by paginated endpoints).
export async function apiEnvelope<T>(
  path: string,
  query?: Record<string, string | number | undefined>,
): Promise<{ data: T; meta?: { total?: number; limit?: number; offset?: number } }> {
  const url = new URL(`${API_URL}${path}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v != null) url.searchParams.set(k, String(v));
    }
  }
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const resp = await fetch(url.toString(), { headers });
  let json: any = null;
  let parseErr: unknown = null;
  try { json = await resp.json(); } catch (e) { parseErr = e; }
  if (!resp.ok) {
    if (resp.status === 401) {
      // Clear both rj_token and rj_auth (Zustand persist) before redirecting
      clearAuthAndRedirect();
    }
    throw new ApiError(json?.error?.code ?? 'UNKNOWN', json?.error?.message ?? `HTTP ${resp.status}`, resp.status);
  }
  if (parseErr) {
    // 2xx with non-JSON body — almost always a misconfigured edge / HTML error page.
    // Surface it instead of silently returning null data.
    throw new ApiError('BAD_RESPONSE', 'Server returned non-JSON response', resp.status);
  }
  return { data: (json?.data ?? json) as T, meta: json?.meta };
}

export const apiClient = {
  get: <T>(path: string, query?: Record<string, string | number | undefined>) =>
    api<T>(path, { query }),
  getEnvelope: apiEnvelope,
  post: <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => api<T>(path, { method: 'DELETE' }),
};
